import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Sparkles, ArrowRight, Palette, User } from 'lucide-react';
export function HeroSection() {
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20" data-id="element-74">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[#0B0B15]" data-id="element-75">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-[128px]" data-id="element-76" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-600/20 rounded-full blur-[128px]" data-id="element-77" />

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" data-id="element-78" />
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-79" />
      </div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-80">
        <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-81">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-82">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6" data-id="element-83">
              <Sparkles className="w-4 h-4 text-yellow-400" data-id="element-84" />
              <span className="text-sm font-medium text-gray-300" data-id="element-85">
                Level up your digital identity
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold font-display leading-tight mb-6" data-id="element-86">
              Your Gaming <br data-id="element-87" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 via-purple-400 to-cyan-400" data-id="element-88">
                Identity
              </span>
              , <br data-id="element-89" />
              Perfectly Crafted
            </h1>

            <p className="text-lg text-gray-400 mb-8 max-w-lg leading-relaxed" data-id="element-90">
              Stop settling for generic presets. Create a unique, hand-crafted
              avatar that represents your true gaming personality across every
              platform.
            </p>

            <div className="flex flex-wrap gap-4" data-id="element-91">
              <Button size="lg" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-93" />} data-id="element-92">
                Start Creating
              </Button>
              <Button variant="outline" size="lg" data-id="element-94">
                View Gallery
              </Button>
            </div>

            <div className="mt-12 flex items-center gap-4 text-sm text-gray-500" data-id="element-95">
              <div className="flex -space-x-3" data-id="element-96">
                {[1, 2, 3, 4].map(i => <div key={i} className={`w-10 h-10 rounded-full border-2 border-[#0B0B15] bg-gradient-to-br ${['from-pink-500 to-purple-500', 'from-cyan-500 to-blue-500', 'from-yellow-500 to-orange-500', 'from-green-500 to-emerald-500'][i - 1]}`} data-id="element-97" />)}
              </div>
              <p data-id="element-98">Trusted by 10,000+ gamers</p>
            </div>
          </motion.div>

          {/* Visual Content */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative h-[500px] hidden lg:block" data-id="element-99">
            {/* Main Avatar Card */}
            <motion.div animate={{
            y: [0, -20, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-96 bg-gradient-to-b from-gray-800 to-gray-900 rounded-3xl border border-white/10 shadow-2xl overflow-hidden" data-id="element-100">
              <div className="h-2/3 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 relative" data-id="element-101">
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-102">
                  <User className="w-32 h-32 text-white/90" data-id="element-103" />
                </div>
              </div>
              <div className="p-6" data-id="element-104">
                <div className="h-4 w-32 bg-white/10 rounded mb-3" data-id="element-105" />
                <div className="h-3 w-24 bg-white/5 rounded" data-id="element-106" />
                <div className="mt-6 flex gap-2" data-id="element-107">
                  <div className="h-8 w-8 rounded-full bg-cyan-500/20 border border-cyan-500/50" data-id="element-108" />
                  <div className="h-8 w-8 rounded-full bg-purple-500/20 border border-purple-500/50" data-id="element-109" />
                  <div className="h-8 w-8 rounded-full bg-pink-500/20 border border-pink-500/50" data-id="element-110" />
                </div>
              </div>
            </motion.div>

            {/* Floating UI Elements */}
            <motion.div animate={{
            y: [0, 15, 0],
            x: [0, 10, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            delay: 1
          }} className="absolute top-20 right-10 bg-[#1A1A2E] p-4 rounded-2xl border border-white/10 shadow-xl" data-id="element-111">
              <Palette className="w-6 h-6 text-fuchsia-400 mb-2" data-id="element-112" />
              <div className="space-y-2" data-id="element-113">
                <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden" data-id="element-114">
                  <div className="w-2/3 h-full bg-gradient-to-r from-fuchsia-500 to-purple-500" data-id="element-115" />
                </div>
                <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden" data-id="element-116">
                  <div className="w-1/2 h-full bg-gradient-to-r from-cyan-500 to-blue-500" data-id="element-117" />
                </div>
              </div>
            </motion.div>

            <motion.div animate={{
            y: [0, -15, 0],
            x: [0, -10, 0]
          }} transition={{
            duration: 7,
            repeat: Infinity,
            delay: 0.5
          }} className="absolute bottom-32 left-0 bg-[#1A1A2E] px-6 py-3 rounded-xl border border-white/10 shadow-xl flex items-center gap-3" data-id="element-118">
              <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" data-id="element-119" />
              <span className="text-sm font-medium text-white" data-id="element-120">
                Online Status: Active
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}